import { FC, useEffect } from 'react'

import { toast } from '@inno-ui/toast'
import { AxiosError } from 'axios'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import {
  useOauthLoginMutation,
  useOauthRedirectMutation
} from 'shared/api/auth/requests'
import { CustomEvents } from 'shared/constants/customEvents'
import { useSearchParams } from 'shared/hooks/useSearchParams'
import { useExternalLoginStore } from 'shared/stores/externalLoginStore'

import { LoginForm, SsoButton } from '..'
import { LoginMode } from '../../../../LoginPage.types'
import { SSO_PROVIDERS } from './LoginView.constants'
import styles from './LoginView.module.scss'
import type { LoginViewProps, SsoProvider } from './LoginView.types'

export const LoginView: FC<LoginViewProps> = ({ loginMode, setLoginMode }) => {
  const { isExternal } = useExternalLoginStore()
  const { t } = useTranslation('auth', {
    keyPrefix: 'new_login'
  })

  const [searchParams, setSearchParams] = useSearchParams<{
    code?: string
    state?: string
  }>()
  const { code, state } = searchParams

  const ssoRedirect = (provider: SsoProvider) => {
    oauthRedirectMutation.mutate({
      auth_method: 'provider',
      provider,
      account_type: 'personal',
      front_redirect_url: window.location.origin + window.location.pathname
    })
  }
  const oauthRedirectMutation = useOauthRedirectMutation({
    onSuccess: data => {
      window.location.href = data.redirect_url
    }
  })

  const oauthLoginMutation = useOauthLoginMutation({
    onError: error => {
      const axiosError = error as AxiosError<{ error?: string }>

      toast.error({
        title: t('login.social_login_error.title'),
        subTitle:
          axiosError.response?.data?.error ??
          t('login.social_login_error.subtitle')
      })
    }
  })

  const { mutate: oauthLogin } = oauthLoginMutation

  useEffect(() => {
    if (!code || !state) return

    oauthLogin({ code, state })
    setSearchParams(
      previous => ({
        ...previous,
        code: undefined,
        state: undefined
      }),
      { replace: true }
    )
  }, [code, oauthLogin, setSearchParams, state])

  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return

    window.parent.dispatchEvent(new CustomEvent(CustomEvents.APP_LOGGED_OUT))
  }, [])

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {loginMode === LoginMode.Regular ? t('title') : t('login.sso.title')}
        </h1>
        {loginMode === LoginMode.Sso && (
          <p className={styles.description}>{t('login.sso.description')}</p>
        )}
      </div>
      <div className={styles.formContainer}>
        <LoginForm mode={loginMode} />
        {!isExternal && (
          <div className={styles.alternativeLogin}>
            <div className={styles.title}>{t('login.or_continue_with')}</div>
            <div className={styles.buttons}>
              <AnimatePresence>
                {loginMode === LoginMode.Regular && (
                  <motion.div
                    className={styles.socialButtons}
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: 'easeInOut'
                    }}
                  >
                    {SSO_PROVIDERS.map(({ provider, img }) => (
                      <SsoButton
                        key={provider}
                        onClick={() => ssoRedirect(provider)}
                        img={img}
                        isLoading={
                          oauthLoginMutation.isPending ||
                          oauthRedirectMutation.isPending
                        }
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              <SsoButton
                onClick={() =>
                  setLoginMode(prev =>
                    prev === LoginMode.Regular
                      ? LoginMode.Sso
                      : LoginMode.Regular
                  )
                }
                isLoading={
                  oauthLoginMutation.isPending ||
                  oauthRedirectMutation.isPending
                }
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}
